import { planningTime } from './planning-date';
import { PlanningConstraints, PlanningSlot } from './planning.types';
import { PlanningIntervalIndex } from './planning-intervals';

interface LoadInterval { start: number; end: number; hours: number; }

/** Resource cells query only overlapping work, rather than reparsing the calendar. */
export class PlanningLoadIndex {
    private readonly reservations: Map<string, PlanningIntervalIndex<LoadInterval>>;
    private readonly capacity: Map<string, PlanningIntervalIndex<LoadInterval>>;
    private readonly slots: PlanningSlot[];
    private readonly basis: PlanningConstraints['time_basis'];
    private readonly unitSeconds: number;

    constructor(snapshot: PlanningConstraints, previous?: PlanningLoadIndex) {
        this.slots = snapshot.slots;
        this.basis = snapshot.time_basis;
        this.unitSeconds = snapshot.unit_seconds;
        this.reservations = this.index(snapshot.reservations.map(row => ({ resource: row.resource_id,
            start: planningTime(row.start_date, snapshot.time_basis), end: planningTime(row.end_date, snapshot.time_basis),
            hours: row.effort_units * snapshot.unit_seconds / 3600 })));
        this.capacity = previous?.slots === snapshot.slots && previous.basis === snapshot.time_basis && previous.unitSeconds === snapshot.unit_seconds
            ? previous.capacity
            : this.index(snapshot.slots.map(row => ({ resource: row.resource_id,
                start: planningTime(row.start_date, snapshot.time_basis), end: planningTime(row.end_date, snapshot.time_basis),
                hours: (row.capacity_units - row.blocked_units) * snapshot.unit_seconds / 3600 })));
    }

    public booked(resourceId: string, start: number, end: number): number { return this.total(this.reservations, resourceId, start, end); }
    public available(resourceId: string, start: number, end: number): number { return this.total(this.capacity, resourceId, start, end); }

    private total(index: Map<string, PlanningIntervalIndex<LoadInterval>>, resourceId: string, start: number, end: number): number {
        return (index.get(resourceId)?.overlapping(start, end) ?? []).reduce((sum, row) =>
            sum + row.hours * (Math.min(row.end, end) - Math.max(row.start, start)) / (row.end - row.start), 0);
    }

    private index(rows: (LoadInterval & { resource: string })[]): Map<string, PlanningIntervalIndex<LoadInterval>> {
        const grouped = new Map<string, LoadInterval[]>();
        for (const row of rows) {
            const group = grouped.get(row.resource);
            if (group) group.push(row); else grouped.set(row.resource, [row]);
        }
        return new Map([...grouped].map(([resource, group]) => [resource, new PlanningIntervalIndex(group, row => row)]));
    }
}
